import { readRefusal } from '@/api/api';

/** The code the plan's generation is refused with when the objective or a noted topic is about leave, health,
 * HR, union or discipline. */
const SENSITIVE_OBJECTIVE_CODE = 'MeetingPlanSensitiveObjective';

/** A noted topic whose title was refused, with the sentences of the title that were. */
export interface SensitiveTopic {
    /** The topic's id, when the server sent it. */
    id: string | null;
    title: string;
    sentences: string[];
}

/** What the server refused, by source: the manager's objective, and each noted topic. */
export interface SensitiveRefusalDetails {
    objectiveSentences: string[];
    topics: SensitiveTopic[];
}

/**
 * The plan was refused before any AI call: these sentences, in the manager's own words, must be rewritten first.
 * Thrown by the agenda generation so the prompt modal can show the objective's sentences under the objective and
 * the noted topics apart, since those are edited in the meeting preparation, not in the modal.
 */
export class SensitiveObjectiveRefusal extends Error {
    readonly objectiveSentences: string[];
    readonly topics: SensitiveTopic[];

    constructor({ objectiveSentences, topics }: SensitiveRefusalDetails) {
        super(SENSITIVE_OBJECTIVE_CODE);
        this.name = 'SensitiveObjectiveRefusal';
        this.objectiveSentences = objectiveSentences;
        this.topics = topics;
    }
}

interface RefusedSentence {
    sentence?: unknown;
    source?: unknown;
    topicId?: unknown;
    topicTitle?: unknown;
}

const strings = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

/** Groups the topic sentences by topic, in the order the server listed them. */
const topicsOf = (refusals: RefusedSentence[]): SensitiveTopic[] => {
    const topics = new Map<string, SensitiveTopic>();
    for (const refusal of refusals) {
        if (refusal.source !== 'topic' || typeof refusal.sentence !== 'string') continue;
        const id = typeof refusal.topicId === 'string' ? refusal.topicId : null;
        const title =
            typeof refusal.topicTitle === 'string' ? refusal.topicTitle : refusal.sentence;
        const key = id ?? title;
        const topic = topics.get(key) ?? { id, title, sentences: [] };
        topic.sentences.push(refusal.sentence);
        topics.set(key, topic);
    }
    return [...topics.values()];
};

/**
 * What was refused when `error` is that refusal (400 with its code), else null. Each sentence comes with its source
 * in `refusals`; a server that sends only `sentences` has them read as the objective's.
 */
export const readSensitiveRefusal = (error: unknown): SensitiveRefusalDetails | null => {
    const refusal = readRefusal(error);
    if (refusal?.status !== 400 || refusal.code !== SENSITIVE_OBJECTIVE_CODE) {
        return null;
    }

    const data = (error as { response?: { data?: { sentences?: unknown; refusals?: unknown } } })
        .response?.data;
    if (!Array.isArray(data?.refusals)) {
        return { objectiveSentences: strings(data?.sentences), topics: [] };
    }

    const refusals = data.refusals.filter(
        (item): item is RefusedSentence => typeof item === 'object' && item !== null,
    );
    return {
        objectiveSentences: refusals
            .filter((item) => item.source === 'objective')
            .map((item) => item.sentence)
            .filter((sentence): sentence is string => typeof sentence === 'string'),
        topics: topicsOf(refusals),
    };
};
